/**
 * Fichiers JS introuvables après un redéploiement (SAAS-984).
 *
 * @remarks
 * Vite construit chaque vue chargée à la demande dans un fichier au nom haché
 * (`EmailSentView-a1b2c3.js`). Un onglet ouvert avant un redéploiement garde
 * les anciens noms : à la navigation suivante, il réclame un fichier qui n'existe
 * plus, et l'import échoue. Sans filet, l'erreur remonte au `RootErrorBoundary`
 * et l'utilisateur voit « Une erreur est survenue » — y compris juste après une
 * inscription réussie, ce qui était le symptôme du ticket.
 *
 * Deux réponses :
 *  - `installChunkReloadGuard` recharge la page **une fois** quand Vite signale
 *    l'échec (`vite:preloadError`) : l'onglet récupère la nouvelle version et la
 *    navigation aboutit.
 *  - `isChunkLoadError` permet aux fallbacks d'afficher un message adapté si le
 *    rechargement n'a pas suffi.
 */

/** Messages d'échec d'import dynamique, par moteur. */
const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i, // helper de préchargement Vite
];

/** L'erreur vient-elle d'un fichier de l'application qui n'a pas pu être chargé ? */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
}

/** Recharge la page. Isolé pour être remplacé dans les tests (jsdom l'interdit). */
export function reloadPage(): void {
  window.location.reload();
}

/** Clé `sessionStorage` : horodatage du dernier rechargement déclenché ici. */
export const CHUNK_RELOAD_KEY = 'saas-front:chunk-reload-at';

/**
 * Délai pendant lequel un second échec ne recharge plus.
 *
 * Si le fichier manque encore juste après le rechargement (réseau coupé,
 * déploiement incomplet), recharger en boucle n'aiderait pas : on laisse
 * l'erreur atteindre le fallback, qui propose de réessayer à la main.
 */
const RELOAD_WINDOW_MS = 10_000;

interface ChunkReloadGuardDeps {
  readonly target: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  readonly storage: () => Storage;
  readonly reload: () => void;
  readonly now: () => number;
}

const defaultDeps: ChunkReloadGuardDeps = {
  target: window,
  storage: () => window.sessionStorage,
  reload: reloadPage,
  now: () => Date.now(),
};

/**
 * Recharge la page une fois quand un fichier de l'application ne se charge pas.
 *
 * @remarks
 * L'événement n'est **pas** annulé (`preventDefault`) : l'erreur poursuit sa
 * route jusqu'au fallback le temps que le rechargement parte. L'annuler ferait
 * résoudre l'import sur `undefined` et planter la vue plus loin, avec un message
 * trompeur.
 *
 * Sans `sessionStorage` accessible, aucun rechargement : sans mémoire du
 * précédent, rien n'empêcherait une boucle.
 *
 * @returns une fonction qui retire l'écoute.
 */
export function installChunkReloadGuard(deps: ChunkReloadGuardDeps = defaultDeps): () => void {
  const onPreloadError = (): void => {
    try {
      const store = deps.storage();
      const now = deps.now();
      const last = Number(store.getItem(CHUNK_RELOAD_KEY) ?? 0);
      if (now - last < RELOAD_WINDOW_MS) return;
      store.setItem(CHUNK_RELOAD_KEY, String(now));
    } catch {
      return;
    }
    deps.reload();
  };

  deps.target.addEventListener('vite:preloadError', onPreloadError);
  return () => {
    deps.target.removeEventListener('vite:preloadError', onPreloadError);
  };
}
